import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Trophy, Heart, Coins, Users, ArrowLeft, Plus, Crown, Clock } from "lucide-react";
import { toast } from "sonner";

const PlyxChallengeDetail = () => {
  const { id } = useParams();
  const { user } = usePlyxAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hearting, setHearting] = useState(null);
  const [joining, setJoining] = useState(false);
  const [myVideos, setMyVideos] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const reload = () => {
    setLoading(true);
    plyxApi.get(`/plyx/challenges/${id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load challenge"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const openVideoPicker = async () => {
    if (!user) { toast.error("Sign in first"); return; }
    setPickerOpen(true);
    if (myVideos.length === 0) {
      try {
        const { data: r } = await plyxApi.get(`/plyx/u/${user.handle}`);
        setMyVideos(r?.videos || []);
      } catch (e) {
        toast.error("Could not load your videos");
      }
    }
  };

  const submitEntry = async (video_id) => {
    setJoining(true);
    try {
      await plyxApi.post(`/plyx/challenges/${id}/join`, { video_id });
      toast.success("You're in! Rally your fans 🎯");
      setPickerOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join");
    } finally {
      setJoining(false);
    }
  };

  const heartEntry = async (entry_id) => {
    if (!user) { toast.error("Sign in to vote"); return; }
    setHearting(entry_id);
    try {
      const { data: r } = await plyxApi.post(`/plyx/challenges/${id}/entries/${entry_id}/heart`);
      setData((d) => d && {
        ...d,
        entries: d.entries.map((e) => e.entry_id === entry_id ? {
          ...e,
          viewer_hearted: r.hearted,
          hearts_count: e.hearts_count + (r.hearted ? 1 : -1),
        } : e),
        total_hearts: d.total_hearts + (r.hearted ? 1 : -1),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vote failed");
    } finally {
      setHearting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center" data-testid="plyx-challenge-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin opacity-60"/>
      </div>
    );
  }
  if (!data) return null;

  const endsAt = data.ends_at ? new Date(data.ends_at) : null;
  const timeLeftMs = endsAt ? endsAt - Date.now() : 0;
  const myEntry = data.entries.find((e) => e.creator?.plyx_user_id === user?.plyx_user_id);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-challenge-detail-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        {data.banner_url && (
          <img src={plyxMediaUrl(data.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40"/>
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.4), rgba(10,10,10,0.95))" }}/>
        <div className="relative max-w-4xl mx-auto px-5 py-10">
          <Link to="/plyx/challenges" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-4" data-testid="plyx-challenge-detail-back">
            <ArrowLeft className="w-4 h-4"/> all challenges
          </Link>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] uppercase tracking-[0.2em] font-bold">{data.template}</span>
            {data.status === "live" && (
              <span className="px-2.5 py-1 rounded-full bg-red-600 text-[10px] uppercase font-bold animate-pulse inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white"/> LIVE
              </span>
            )}
            {data.status === "ended" && (
              <span className="px-2.5 py-1 rounded-full bg-black/60 border border-[#FFD700]/40 text-[10px] uppercase font-bold inline-flex items-center gap-1">
                <Trophy className="w-3 h-3 text-[#FFD700]"/> Ended
              </span>
            )}
            {data.country_tags?.map((cc) => (
              <span key={cc} className="px-2 py-0.5 rounded-full bg-white/10 text-xs font-semibold">{cc}</span>
            ))}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl tracking-tight leading-tight">{data.title}</h1>
          {data.description && (
            <p className="text-sm text-white/75 mt-3 whitespace-pre-wrap max-w-2xl">{data.description}</p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-5 text-sm">
            <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-white/60"/> {data.entry_count} {data.entry_count === 1 ? "entry" : "entries"}</span>
            <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4 text-[#FF2D7A]"/> {data.total_hearts}</span>
            {data.prize_pool_coins > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[#FFB42D] font-semibold">
                <Coins className="w-4 h-4"/> {data.prize_pool_coins.toLocaleString()} prize
              </span>
            )}
            {data.status === "live" && timeLeftMs > 0 && (
              <span className="inline-flex items-center gap-1.5 text-white/70">
                <Clock className="w-4 h-4"/>
                {Math.floor(timeLeftMs / (1000 * 60 * 60 * 24)) > 0
                  ? `${Math.floor(timeLeftMs / (1000 * 60 * 60 * 24))}d left`
                  : `${Math.floor(timeLeftMs / (1000 * 60 * 60))}h left`}
              </span>
            )}
            <span className="ml-auto text-white/60 text-xs">by @{data.creator_handle}</span>
          </div>

          {data.status === "live" && user && !myEntry && (
            <button
              type="button"
              onClick={openVideoPicker}
              data-testid="plyx-challenge-join-btn"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[#0A0A0A] text-sm"
              style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              <Plus className="w-4 h-4"/> Enter this challenge
            </button>
          )}
          {myEntry && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00E0C7]/15 border border-[#00E0C7]/30 text-[#00E0C7] text-xs font-semibold" data-testid="plyx-challenge-myentry-badge">
              <Trophy className="w-3.5 h-3.5"/> You're entry #{myEntry.rank} · {myEntry.hearts_count} ❤
            </div>
          )}
        </div>
      </div>

      {/* Video picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-[75] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setPickerOpen(false)}>
          <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-3xl p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-3">Pick a video to enter</h3>
            {myVideos.length === 0 ? (
              <div className="text-white/60 text-sm">
                You have no videos yet.{" "}
                <Link to="/plyx/upload" className="text-[#FFB42D] underline">Upload one</Link>.
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {myVideos.map((v) => (
                  <button
                    key={v.video_id}
                    type="button"
                    disabled={joining}
                    onClick={() => submitEntry(v.video_id)}
                    data-testid={`plyx-challenge-pick-video-${v.video_id}`}
                    className="aspect-[9/16] rounded-lg overflow-hidden bg-black/50 relative border border-white/10 hover:border-[#FFB42D] disabled:opacity-50"
                  >
                    {v.thumbnail_url
                      ? <img src={plyxMediaUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover"/>
                      : <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">no thumb</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="max-w-4xl mx-auto px-5 py-8 pb-24">
        <h2 className="font-display text-xl mb-4 inline-flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#FFB42D]"/> {data.status === "ended" ? "Champions" : "Leaderboard"}
        </h2>
        {data.entries.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center text-white/60" data-testid="plyx-challenge-no-entries">
            No entries yet — be the first.
          </div>
        ) : (
          <div className="space-y-3" data-testid="plyx-challenge-leaderboard">
            {data.entries.map((e) => (
              <EntryRow
                key={e.entry_id}
                entry={e}
                onHeart={() => heartEntry(e.entry_id)}
                hearting={hearting === e.entry_id}
                challengeStatus={data.status}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EntryRow = ({ entry, onHeart, hearting, challengeStatus }) => {
  const top3 = entry.rank <= 3;
  const rankColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
  return (
    <div
      data-testid={`plyx-challenge-entry-${entry.entry_id}`}
      className={`flex items-center gap-3 p-3 rounded-2xl border ${top3 ? "border-[#FFB42D]/40 bg-[#FFB42D]/5" : "border-white/10 bg-white/5"}`}
    >
      <div className="font-display text-2xl w-9 text-center" style={{ color: top3 ? rankColors[entry.rank - 1] : "rgba(255,255,255,0.4)" }}>
        {top3 ? <Crown className="w-6 h-6 mx-auto"/> : entry.rank}
      </div>
      <Link to={`/plyx/v/${entry.video_id}`} className="w-14 h-20 rounded-lg overflow-hidden bg-black shrink-0">
        {entry.thumbnail_url
          ? <img src={plyxMediaUrl(entry.thumbnail_url)} alt="" className="w-full h-full object-cover"/>
          : <div className="w-full h-full flex items-center justify-center text-white/20 text-[10px]">video</div>}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/plyx/u/${entry.creator.handle}`} className="font-semibold text-sm truncate block hover:underline">
          @{entry.creator.handle}
        </Link>
        {entry.creator.country && (
          <div className="text-[10px] text-white/40 uppercase tracking-wider">{entry.creator.country}</div>
        )}
        {entry.caption && (
          <div className="text-xs text-white/60 line-clamp-1 mt-0.5">{entry.caption}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-base">{entry.hearts_count} ❤</div>
        {entry.gift_coins > 0 && (
          <div className="text-[10px] text-[#FFB42D]">{entry.gift_coins.toLocaleString()} coins</div>
        )}
      </div>
      {challengeStatus !== "ended" && (
        <button
          type="button"
          onClick={onHeart}
          disabled={hearting}
          data-testid={`plyx-challenge-heart-${entry.entry_id}`}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shrink-0 ${
            entry.viewer_hearted ? "bg-[#FF2D7A] text-white" : "bg-white/10 hover:bg-white/20 text-white"
          }`}
          aria-label="Heart vote"
        >
          {hearting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Heart className={`w-4 h-4 ${entry.viewer_hearted ? "fill-white" : ""}`}/>}
        </button>
      )}
    </div>
  );
};

export default PlyxChallengeDetail;
